import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { hhmm, todayIE } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Empty } from "@/components/Bits";

const PALETTE = ["#3b82f6", "#22c55e", "#f97316", "#a855f7", "#ec4899", "#eab308", "#14b8a6", "#ef4444", "#6366f1", "#84cc16"];
const colour = (key) => PALETTE[[...String(key)].reduce((a, c) => a + c.charCodeAt(0), 0) % PALETTE.length];
const AVATAR = ["bg-amber-500", "bg-sky-600", "bg-indigo-600", "bg-emerald-600", "bg-rose-600", "bg-slate-600"];
const initials = (n) => n.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();
const hms = (s) => `${String(Math.floor(s / 3600)).padStart(2, "0")}:${String(Math.floor((s % 3600) / 60)).padStart(2, "0")}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

function ago(iso) {
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 3600) return `${Math.max(1, Math.round(d / 60))} min ago`;
  if (d < 86400) return `${Math.round(d / 3600)}h ago`;
  const days = Math.round(d / 86400);
  return days < 7 ? `${days} day${days > 1 ? "s" : ""} ago` : new Date(iso).toLocaleDateString("en-IE", { timeZone: "Europe/Dublin", day: "numeric", month: "short", year: "numeric" });
}

function Activity({ p }) {
  const a = p.current || p.last;
  if (!a) return <span className="text-muted-foreground text-sm">No activity yet</span>;
  return (
    <div className="min-w-0">
      <div className="text-xs text-muted-foreground">{a.stage_name || "(no task)"}</div>
      <div className="text-sm truncate"><span className="inline-block w-2 h-2 rounded-full mr-1.5" style={{ background: colour(a.job_id || "BUSINESS") }} />
        <span className="font-semibold">{a.job_name}</span>{a.customer_name && <span className="text-muted-foreground"> · {a.customer_name}</span>}</div>
    </div>
  );
}

function Row({ p, max }) {
  const a = p.current || p.last;
  const live = p.current?.status === "running";
  return (
    <tr className="border-b border-border" data-testid={`team-row-${p.user_id}`}>
      <td className="px-4 py-3"><div className="flex items-center gap-2.5">
        <span className={`w-8 h-8 rounded-md flex items-center justify-center text-xs font-bold text-white ${AVATAR[p.name.length % AVATAR.length]}`}>{initials(p.name)}</span>
        <div><div className="font-semibold text-sm">{p.name}</div>
          <div className={`text-[10px] font-mono uppercase ${p.shift === "in" ? "text-emerald-400" : p.shift === "break" ? "text-sky-400" : "text-muted-foreground"}`} data-testid={`team-shift-${p.user_id}`}>{p.shift === "in" ? "Clocked in" : p.shift === "break" ? "On break" : "Clocked out"}</div></div>
      </div></td>
      <td className="px-4 py-3 max-w-xs"><Activity p={p} /></td>
      <td className="px-4 py-3 font-mono-num text-sm whitespace-nowrap" data-testid={`team-duration-${p.user_id}`}>{p.current ? hms(p.current.elapsed_seconds) : a ? `${hhmm(a.minutes)}` : ""}</td>
      <td className="px-4 py-3 text-sm whitespace-nowrap">{live ? <span className="text-emerald-400 font-semibold">● Working now</span> : p.current ? <span className="text-amber-400">Paused</span> : a ? <span className="text-muted-foreground">{ago(a.at)}</span> : ""}</td>
      <td className="px-4 py-3 font-mono-num text-sm text-right" data-testid={`team-total-${p.user_id}`}>{hhmm(p.total_minutes)}</td>
      <td className="px-4 py-3 w-[28%] min-w-[160px]">
        <div className="h-5 rounded bg-accent/50 overflow-hidden flex" style={{ width: `${max ? Math.max((p.total_minutes / max) * 100, p.total_minutes ? 4 : 0) : 0}%` }}>
          {p.segments.map((s) => <div key={s.job_id || "b"} title={`${s.job_name}: ${hhmm(s.minutes)}`} style={{ flex: s.minutes, background: colour(s.job_id || "BUSINESS") }} />)}
        </div>
      </td>
    </tr>
  );
}

export default function TeamActivity() {
  const [day, setDay] = useState(todayIE());
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get("/team-activity", { params: { date: day } }).then((r) => setData(r.data)).catch(() => {}), [day]);
  useEffect(() => { load(); const i = setInterval(load, 30000); return () => clearInterval(i); }, [load]);
  const max = data ? Math.max(...data.people.map((p) => p.total_minutes), 0) : 0;
  return (
    <div data-testid="team-activity">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-sm text-muted-foreground">Total tracked on</span>
        <Input type="date" value={day} onChange={(e) => setDay(e.target.value || todayIE())} className="w-44 h-9" data-testid="team-activity-date" />
      </div>
      {!data ? <div className="text-muted-foreground">Loading…</div> : data.people.length === 0 ? <Empty text="No staff yet." /> : (
        <div className="card-industrial overflow-x-auto">
          <table className="w-full">
            <thead className="bg-accent/40 text-[11px] font-mono uppercase tracking-wider text-muted-foreground">
              <tr><th className="text-left px-4 py-3">Team member</th><th className="text-left px-4 py-3">Latest activity</th><th className="text-left px-4 py-3">Time</th>
                <th className="text-left px-4 py-3">When</th><th className="text-right px-4 py-3">Total ({data.is_today ? "today" : new Date(day + "T12:00:00").toLocaleDateString("en-IE", { day: "numeric", month: "short" })})</th><th className="px-4 py-3" /></tr>
            </thead>
            <tbody>{data.people.map((p) => <Row key={p.user_id} p={p} max={max} />)}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}
